import {
  CSSProperties,
  ReactNode,
  createContext,
  useContext,
  useMemo,
  useState,
} from 'react'
import useMousePosition from './useMousePosition'

const TooltipContext = createContext<{
  openTooltip: ({
    content,
    style,
  }: {
    content: ReactNode
    style: CSSProperties
  }) => void
  closeTooltip: () => void
}>({
  openTooltip: () => {},
  closeTooltip: () => {},
})

export function useTooltipContext() {
  const tooltipContext = useContext(TooltipContext)

  return tooltipContext
}

export default function CustomTooltip({ children }: { children: ReactNode }) {
  const emptyTooltip = {
    open: false,
    content: undefined as ReactNode,
    style: {} as CSSProperties,
  }

  const [tooltip, setTooltip] = useState(emptyTooltip)

  const openTooltip = ({
    content,
    style,
  }: {
    content: ReactNode
    style: React.CSSProperties
  }) => {
    setTooltip({
      open: true,
      content,
      style,
    })
  }

  const closeTooltip = () => {
    setTooltip(emptyTooltip)
  }

  return (
    <div style={{ height: '100%', width: '100%' }}>
      <Tooltip tooltip={tooltip} />
      <TooltipProvider openTooltip={openTooltip} closeTooltip={closeTooltip}>
        {children}
      </TooltipProvider>
    </div>
  )
}

const Tooltip = ({
  tooltip,
}: {
  tooltip: { open: boolean; content: ReactNode; style: React.CSSProperties }
}) => {
  const position = useMousePosition()

  const left = tooltip.open ? position.x : -9999

  const top = tooltip.open ? position.y : -9999

  return (
    <div
      style={{
        position: 'fixed',
        left: left + 5,
        top: top + 5,
        zIndex: 9999,
        ...tooltip.style,
      }}>
      {tooltip.content}
    </div>
  )
}

function TooltipProvider({
  children,
  openTooltip,
  closeTooltip,
}: {
  children: ReactNode
  openTooltip: ({
    content,
    style,
  }: {
    content: ReactNode
    style: React.CSSProperties
  }) => void
  closeTooltip: () => void
}) {
  const tooltipContext = useMemo(() => {
    return {
      openTooltip,
      closeTooltip,
    }
  }, [openTooltip, closeTooltip])

  return (
    <TooltipContext.Provider value={tooltipContext}>
      {children}
    </TooltipContext.Provider>
  )
}
